import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { getRmaJobs } from '../utils/rmaDb';
import type { RmaJob, JobNote } from '../utils/rmaDb';
import {
  Wrench,
  Clock,
  CheckCircle2,
  Truck,
  AlertTriangle,
  Activity,
  Plus,
  ArrowRight,
  TrendingUp,
  Laptop,
  Smartphone,
  Tablet,
  Gamepad2,
  HelpCircle
} from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<RmaJob[]>([]);
  const userEmail = localStorage.getItem('user_email') || 'Technician';
  const userRole = localStorage.getItem('user_role') || 'Admin';

  useEffect(() => {
    // Fetch jobs from local storage database
    setJobs(getRmaJobs());
  }, []);

  // Compute metrics
  const activeJobs = jobs.filter(j => j.status !== 'Delivered');
  const totalActive = activeJobs.length;
  const pendingDiagnostics = jobs.filter(j => j.status === 'Diagnostics').length;
  const inRepair = jobs.filter(j => j.status === 'Repairing').length;
  const readyForPickup = jobs.filter(j => j.status === 'Ready').length;
  const delivered = jobs.filter(j => j.status === 'Delivered').length;

  // Urgent alerts (High or Urgent priority and not delivered)
  const urgentJobs = jobs.filter(j => (j.priority === 'Urgent' || j.priority === 'High') && j.status !== 'Delivered');

  // Gather recent notes / activities across all tickets
  const recentActivities: { job: RmaJob; note: JobNote }[] = [];
  jobs.forEach(job => {
    job.notes.forEach(note => {
      recentActivities.push({ job, note });
    });
  });
  
  // Sort activities by timestamp descending
  recentActivities.sort((a, b) => new Date(b.note.timestamp).getTime() - new Date(a.note.timestamp).getTime());
  const displayActivities = recentActivities.slice(0, 5);

  // Category counts
  const getCategoryCount = (cat: string) => jobs.filter(j => j.deviceCategory === cat).length;
  const categories = [
    { name: 'Phone', count: getCategoryCount('Phone'), icon: Smartphone, color: 'text-blue-500 bg-blue-50' },
    { name: 'Laptop', count: getCategoryCount('Laptop'), icon: Laptop, color: 'text-purple-500 bg-purple-50' },
    { name: 'Tablet', count: getCategoryCount('Tablet'), icon: Tablet, color: 'text-amber-500 bg-amber-50' },
    { name: 'Console', count: getCategoryCount('Console'), icon: Gamepad2, color: 'text-indigo-500 bg-indigo-50' },
    { name: 'Other', count: getCategoryCount('Other'), icon: HelpCircle, color: 'text-slate-500 bg-slate-50' },
  ];

  // Helper for status badge colors
  const getStatusBadge = (status: RmaJob['status']) => {
    const styles = {
      'Intake': 'bg-slate-100 text-slate-800 border-slate-200',
      'Diagnostics': 'bg-amber-100 text-amber-800 border-amber-200',
      'Awaiting Parts': 'bg-orange-100 text-orange-800 border-orange-200',
      'Repairing': 'bg-blue-100 text-blue-800 border-blue-200',
      'Testing': 'bg-purple-100 text-purple-800 border-purple-200',
      'Ready': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Delivered': 'bg-teal-100 text-teal-800 border-teal-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  // Helper for priority badge colors
  const getPriorityBadge = (priority: RmaJob['priority']) => {
    const styles = {
      'Low': 'bg-gray-50 text-gray-600',
      'Medium': 'bg-blue-50 text-blue-600',
      'High': 'bg-orange-50 text-orange-600',
      'Urgent': 'bg-rose-50 text-rose-600 border border-rose-100 animate-pulse'
    };
    return styles[priority] || 'bg-gray-50 text-gray-600';
  };

  // Estimate total revenues
  const totalRevenue = jobs.reduce((acc, curr) => acc + (curr.status === 'Delivered' ? curr.actualCost : curr.advancePayment), 0);
  const pendingRevenue = jobs
    .filter(j => j.status !== 'Delivered')
    .reduce((acc, curr) => acc + (curr.actualCost || curr.estimatedCost) - curr.advancePayment, 0);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full text-slate-800">
      <div className="flex flex-1 w-full">
        <Sidebar />
        
        <main className="flex-1 p-6 md:p-8 w-full max-h-screen overflow-y-auto">
          <div className="w-full space-y-8">
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 bg-gradient-to-r from-slate-900 to-indigo-900 bg-clip-text text-transparent">
                  Repair Operations
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Welcome back, <span className="font-semibold text-slate-700">{userEmail}</span> ({userRole}) • Active jobs monitoring panel.
                </p>
              </div>
              <button
                onClick={() => navigate('/new-rma-job')}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Plus className="h-4.5 w-4.5 mr-2" />
                New Intake Ticket
              </button>
            </div>

            {/* Quick Metrics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              
              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-4">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                  <Activity className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Active Jobs</p>
                  <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{totalActive}</h3>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-4">
                <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Diagnostics</p>
                  <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{pendingDiagnostics}</h3>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                  <Wrench className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">In Repair</p>
                  <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{inRepair}</h3>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-4">
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Ready</p>
                  <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{readyForPickup}</h3>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-4">
                <div className="p-3 bg-teal-50 text-teal-600 rounded-xl">
                  <Truck className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Delivered</p>
                  <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{delivered}</h3>
                </div>
              </div>
            </div>

            {/* Financial Overview & Categories Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Financial Box */}
              <div className="lg:col-span-1 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl p-6 text-white shadow-lg border border-slate-800 flex flex-col justify-between relative overflow-hidden">
                <div className="absolute right-0 top-0 translate-x-10 -translate-y-10 w-40 h-40 bg-indigo-500 rounded-full opacity-10 blur-xl"></div>
                
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-xs font-semibold uppercase tracking-wider bg-white bg-opacity-10 px-2.5 py-1 rounded-md text-indigo-200">Financials Overview</span>
                    <TrendingUp className="h-5 w-5 text-indigo-300" />
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wider">Collected Revenue</p>
                      <h2 className="text-3xl font-extrabold text-white mt-1">${totalRevenue.toLocaleString()}</h2>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wider">Pending (Awaiting Pickup/Finish)</p>
                      <h2 className="text-xl font-bold text-indigo-200 mt-0.5">${pendingRevenue.toLocaleString()}</h2>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-800 mt-6 pt-4 flex items-center justify-between">
                  <span className="text-xs text-slate-400">Calculated from local db</span>
                  <button 
                    onClick={() => navigate('/rma-jobs')}
                    className="text-xs font-semibold text-indigo-300 hover:text-indigo-100 flex items-center gap-1 transition-colors"
                  >
                    Manage billing <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* Categories Widget */}
              <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Repairs by Category</h3>
                  <p className="text-xs text-slate-400 mb-6">Distribution of devices currently in the system.</p>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                    {categories.map((cat, idx) => (
                      <div key={idx} className="flex flex-col items-center justify-center p-4 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors">
                        <div className={`p-3 rounded-lg ${cat.color} mb-3`}>
                          <cat.icon className="h-5 w-5" />
                        </div>
                        <span className="text-xs font-medium text-slate-500">{cat.name}</span>
                        <span className="text-lg font-extrabold text-slate-800 mt-1">{cat.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-slate-100 mt-6 pt-4 flex justify-between items-center">
                  <span className="text-xs text-slate-400">Filters jobs in repairs list</span>
                  <button
                    onClick={() => navigate('/rma-jobs')}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    View active directory <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>

            {/* Alert List & Recent Notes Timeline */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Urgent Jobs Section */}
              <div className="lg:col-span-1 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-5 w-5 text-rose-500" />
                  <h3 className="text-lg font-bold text-slate-900">High-Priority Alerts</h3>
                </div>
                
                {urgentJobs.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-slate-100 rounded-xl">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-500">No urgent alerts</p>
                    <p className="text-xs text-slate-400 mt-0.5">All priority tickets are normal.</p>
                  </div>
                ) : (
                  <div className="space-y-3.5 max-h-[300px] overflow-y-auto pr-1">
                    {urgentJobs.map(job => (
                      <div 
                        key={job.id} 
                        onClick={() => navigate(`/rma-jobs/${job.id}`)}
                        className="p-3 bg-rose-50/50 hover:bg-rose-50 border border-rose-100 rounded-xl transition-all cursor-pointer flex justify-between items-start gap-2"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-700">{job.id}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${getPriorityBadge(job.priority)}`}>
                              {job.priority}
                            </span>
                          </div>
                          <h4 className="text-sm font-semibold text-slate-800 truncate mt-1">
                            {job.deviceBrand} {job.deviceModel}
                          </h4>
                          <p className="text-xs text-slate-400 truncate mt-0.5">
                            Customer: {job.customerName}
                          </p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${getStatusBadge(job.status)}`}>
                          {job.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Aggregated Technician Log Feed */}
              <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center mb-5">
                    <div className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-indigo-500" />
                      <h3 className="text-lg font-bold text-slate-900">Recent Repair Updates & Notes</h3>
                    </div>
                    <span className="text-xs text-slate-400">Live feeds</span>
                  </div>

                  {displayActivities.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <p>No activity logged yet.</p>
                    </div>
                  ) : (
                    <div className="relative border-l border-slate-100 pl-4 ml-3 space-y-5">
                      {displayActivities.map(({ job, note }) => (
                        <div key={note.id} className="relative">
                          {/* Dot marker */}
                          <div className={`absolute -left-[24px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white ${
                            note.type === 'system' 
                              ? 'bg-slate-400' 
                              : note.type === 'tech' 
                                ? 'bg-blue-500' 
                                : 'bg-emerald-500'
                          }`} />
                          
                          <div className="flex justify-between items-start gap-4">
                            <div>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-xs font-semibold text-slate-700">{note.author}</span>
                                <span className="text-xs text-slate-400">on</span>
                                <span 
                                  onClick={() => navigate(`/rma-jobs/${job.id}`)}
                                  className="text-xs font-bold text-blue-600 hover:underline cursor-pointer"
                                >
                                  {job.id} ({job.deviceBrand} {job.deviceModel})
                                </span>
                              </div>
                              <p className="text-xs text-slate-600 mt-1 italic font-medium bg-slate-50 p-2 rounded-lg border border-slate-100">
                                {note.text}
                              </p>
                            </div>
                            <span className="text-[10px] text-slate-400 whitespace-nowrap">
                              {new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-100 mt-6 pt-4 flex justify-end">
                  <button
                    onClick={() => navigate('/rma-jobs')}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    View jobs board <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>

            {/* Quick overview of all active tickets */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Current Repairs Board</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Quick lookup table for active diagnostics and fixes.</p>
                </div>
                <button
                  onClick={() => navigate('/rma-jobs')}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg"
                >
                  View Directory
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">
                      <th className="py-3.5 px-6">RMA Ticket</th>
                      <th className="py-3.5 px-6">Customer</th>
                      <th className="py-3.5 px-6">Device</th>
                      <th className="py-3.5 px-6">Priority</th>
                      <th className="py-3.5 px-6">Status</th>
                      <th className="py-3.5 px-6">Assigned Tech</th>
                      <th className="py-3.5 px-6">Updated</th>
                      <th className="py-3.5 px-6">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {activeJobs.slice(0, 5).map((job) => (
                      <tr 
                        key={job.id} 
                        className="hover:bg-slate-50/70 transition-colors"
                      >
                        <td className="py-3.5 px-6 font-bold text-slate-900">{job.id}</td>
                        <td className="py-3.5 px-6">
                          <div className="font-semibold text-slate-800">{job.customerName}</div>
                          <div className="text-xs text-slate-400">{job.customerPhone}</div>
                        </td>
                        <td className="py-3.5 px-6">
                          <div className="font-semibold text-slate-700">{job.deviceBrand} {job.deviceModel}</div>
                          <div className="text-xs text-slate-400">{job.deviceCategory} • S/N: {job.serialNumber}</div>
                        </td>
                        <td className="py-3.5 px-6">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${getPriorityBadge(job.priority)}`}>
                            {job.priority}
                          </span>
                        </td>
                        <td className="py-3.5 px-6">
                          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${getStatusBadge(job.status)}`}>
                            {job.status}
                          </span>
                        </td>
                        <td className="py-3.5 px-6 text-slate-600 font-medium">{job.assignedTo}</td>
                        <td className="py-3.5 px-6 text-slate-400 text-xs">
                          {new Date(job.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="py-3.5 px-6">
                          <button
                            onClick={() => navigate(`/rma-jobs/${job.id}`)}
                            className="text-xs font-semibold text-blue-600 hover:text-indigo-700 bg-blue-50 hover:bg-blue-100/70 px-3 py-1.5 rounded-lg transition-colors"
                          >
                            Edit Notes
                          </button>
                        </td>
                      </tr>
                    ))}
                    {activeJobs.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-12 text-center text-slate-400">
                          No active repairs in progress.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default Home;
